/**
 * Lexical Rich Text Editor
 * 
 * A modular rich text editor built with Lexical.
 * Uses plugin-based architecture for clean separation of concerns.
 */
import React, { useCallback, useState } from 'react';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { HeadingNode } from '@lexical/rich-text';
import { ListNode, ListItemNode } from '@lexical/list';
import { ExtendedTextNode } from './nodes/ExtendedTextNode';

// Custom plugins
import ToolbarPlugin from './plugins/ToolbarPlugin';
import InitialContentPlugin from './plugins/InitialContentPlugin';
import HtmlExportPlugin from './plugins/HtmlExportPlugin';
import EditorTheme from './plugins/EditorTheme';

// Styles
import './LexicalEditor.scss';

const placeholder = 'Write your custom content here...';

function onError(error) {
    console.error('Lexical Editor Error:', error);
}

/**
 * LexicalEditor Component
 * 
 * @param {string} value - Initial HTML content
 * @param {function} onChange - Callback when content changes (receives HTML string)
 * @param {string} placeholder - Placeholder text
 */
const LexicalEditor = ({ value, onChange, placeholder: customPlaceholder, namespace }) => {
    const [editorNamespace] = useState(() => namespace || `theme-editor-${Math.random().toString(36).slice(2)}`);
    const initialConfig = {
        namespace: editorNamespace,
        theme: EditorTheme,
        onError,
        nodes: [
            ExtendedTextNode,
            HeadingNode,
            ListNode,
            ListItemNode,
        ],
    };

    const handleChange = useCallback((html) => {
        if (onChange) {
            onChange(html);
        }
    }, [onChange]);

    return (
        <LexicalComposer key={editorNamespace} initialConfig={initialConfig}>
            <div className="editor-container">
                <ToolbarPlugin />
                <div className="editor-inner">
                    <RichTextPlugin
                        contentEditable={
                            <ContentEditable
                                className="editor-input"
                                aria-placeholder={customPlaceholder || placeholder}
                            />
                        }
                        placeholder={
                            <div className="editor-placeholder">
                                {customPlaceholder || placeholder}
                            </div>
                        }
                        ErrorBoundary={LexicalErrorBoundary}
                    />
                    <HistoryPlugin />
                    <ListPlugin />
                    <InitialContentPlugin initialHtml={value} />
                    <HtmlExportPlugin onChange={handleChange} />
                </div>
            </div>
        </LexicalComposer>
    );
};

export default LexicalEditor;
